<template>
    <b-modal id="main-order-modal" size="xl" :title="modalTitle" no-close-on-esc no-close-on-backdrop>
        <div class="main-order-form">
            <!-- Üst Bilgi Kartı -->
            <b-card no-body class="mb-2">
                <b-card-body class="p-2">
                    <b-row>
                        <b-col md="4">
                            <b-form-group label="Ana Sipariş Numarası" label-for="main-order-number" class="mb-0">
                                <b-form-input id="main-order-number" :value="value.MainOrderNumber" readonly />
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group label="Toplam Miktar [lt]" label-for="total-quantity" class="mb-0 is-required">
                                <v-currency-input :value="value.TotalQuantity" @update-value="$emit('update:totalQuantity', $event)" :disabled="hasSubOrders" />
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group label="Sevkiyat/Alt Sipariş Sayısı" label-for="shipment-count" class="mb-0 is-required">
                                <b-form-input id="shipment-count" type="number" :value="value.ShipmentCount" @input="$emit('update:shipmentCount', parseInt($event) || null)" :disabled="hasSubOrders" min="1" />
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row class="mt-2" v-if="!hasSubOrders">
                        <b-col class="text-center">
                            <b-button variant="primary" size="sm" @click="createSubOrderTemplates" :disabled="!canCreateTemplates">
                                <feather-icon icon="PlusIcon" size="14" class="mr-50" />
                                Alt Sipariş Şablonlarını Oluştur
                            </b-button>
                        </b-col>
                    </b-row>
                </b-card-body>
            </b-card>

            <!-- Alt Siparişler Tablosu -->
            <b-card no-body v-if="subOrders.length > 0">
                <b-card-header class="p-1 d-flex justify-content-between align-items-center">
                    <span class="font-weight-bold">Alt Siparişler</span>
                    <b-badge :variant="quantityMatch ? 'success' : 'danger'" pill> {{ totalSubOrderQuantity.toLocaleString("tr-TR") }} / {{ (value.TotalQuantity || 0).toLocaleString("tr-TR") }} lt </b-badge>
                </b-card-header>

                <b-table-simple small bordered striped responsive class="mb-0">
                    <b-thead head-variant="light">
                        <b-tr>
                            <b-th class="text-center" style="width: 50px">#</b-th>
                            <b-th style="width: 160px">Alt Sipariş No</b-th>
                            <b-th style="width: 120px">Yakıt Tipi</b-th>
                            <b-th style="width: 130px">Miktar [lt]</b-th>
                            <b-th>Termin Tarihi</b-th>
                            <b-th class="text-center" style="width: 80px">Durum</b-th>
                            <b-th class="text-center" style="width: 100px">İşlem</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="(sub, index) in subOrders" :key="index">
                            <b-td class="text-center">{{ index + 1 }}</b-td>
                            <b-td>
                                <code v-if="sub.OrderNumber" class="text-success">{{ sub.OrderNumber }}</code>
                                <span v-else v-b-tooltip.hover title="Sipariş numarası kayıt sonrası sistem tarafından otomatik atanacaktır">
                                    <code class="text-muted" style="cursor: help">Otomatik atanacak</code>
                                </span>
                            </b-td>
                            <b-td class="text-center">
                                <span v-if="sub.FuelTypeName" class="badge" :class="getFuelTypeBadgeClass(sub.FuelTypeName)">
                                    {{ sub.FuelTypeName }}
                                </span>
                                <span v-else class="text-muted font-small-2">-</span>
                            </b-td>
                            <b-td>
                                <v-currency-input :value="sub.OrderQuantity" @update-value="updateSubOrderField(index, 'OrderQuantity', $event)" size="sm" />
                            </b-td>
                            <b-td>
                                <v-date-picker :value="sub.OrderDate" @input="updateSubOrderField(index, 'OrderDate', $event)" size="sm" placeholder="Tarih seçin" />
                            </b-td>
                            <b-td class="text-center">
                                <b-badge :variant="isSubOrderComplete(sub) ? 'success' : 'secondary'">
                                    {{ isSubOrderComplete(sub) ? "Hazır" : "Bekliyor" }}
                                </b-badge>
                            </b-td>
                            <b-td class="text-center">
                                <b-button-group size="sm">
                                    <b-button variant="outline-primary" @click="editSubOrder(index)">
                                        <feather-icon icon="EditIcon" size="14" />
                                    </b-button>
                                    <b-button v-if="isSubOrderComplete(sub)" variant="outline-info" @click="viewSubOrder(index)">
                                        <feather-icon icon="EyeIcon" size="14" />
                                    </b-button>
                                </b-button-group>
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <b-card-footer class="py-50 px-75 bg-light">
                    <small class="text-muted">
                        <feather-icon icon="InfoIcon" size="12" class="mr-50" />
                        Her alt sipariş için <strong>Düzenle</strong> butonuyla detayları doldurun. Yakıt Tipi, Tedarikçi, Şube, Miktar, Termin Tarihi alanları doldurulmalı ve miktarların toplamı ana siparişe eşit olmalıdır.
                    </small>
                </b-card-footer>
            </b-card>

            <!-- Validasyon Hataları -->
            <b-alert variant="danger" :show="!!validationError" class="mb-0 p-50">
                <feather-icon icon="AlertTriangleIcon" class="mr-50" />
                {{ validationError }}
            </b-alert>
        </div>

        <!-- Footer -->
        <template #modal-footer>
            <b-row class="w-100 m-0">
                <b-col class="text-right p-0">
                    <b-button variant="success" size="sm" @click="saveMainOrder" :disabled="!canSave">
                        <feather-icon icon="SaveIcon" size="14" class="mr-50" />
                        Kaydet
                    </b-button>
                </b-col>
            </b-row>
        </template>
    </b-modal>
</template>

<script>
import { VBTooltip, BModal, BCard, BCardBody, BCardHeader, BCardFooter, BRow, BCol, BFormGroup, BFormInput, BButton, BButtonGroup, BBadge, BAlert, BTableSimple, BThead, BTbody, BTfoot, BTr, BTh, BTd } from "bootstrap-vue";
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue";
import VCurrencyInput from "@/components/VCurrencyInput.vue";
import VDatePicker from "@/components/VDatePicker.vue";

export default {
    name: "FuelStorageMainOrderModal",
    components: {
        BModal,
        BCard,
        BBadge,
        BCardBody,
        BCardHeader,
        BCardFooter,
        BRow,
        BCol,
        BFormGroup,
        BFormInput,
        BButton,
        BButtonGroup,
        BAlert,
        BTableSimple,
        BThead,
        BTbody,
        BTr,
        BTh,
        BTd,
        FeatherIcon,
        VCurrencyInput,
        VDatePicker,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        value: {
            type: Object,
            default: () => ({
                MainOrderNumber: "",
                TotalQuantity: null,
                ShipmentCount: null,
                SubOrders: [],
            }),
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
        isReadOnly: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        modalTitle() {
            if (!this.value) return "Yeni Ana Sipariş Oluştur";
            if (this.isReadOnly) return `Ana Sipariş Detay - ${this.value.MainOrderNumber}`;
            return this.isEdit ? `Ana Sipariş Güncelle - ${this.value.MainOrderNumber}` : "Yeni Ana Sipariş Oluştur";
        },
        subOrders() {
            return this.value?.SubOrders || [];
        },
        hasSubOrders() {
            return this.subOrders.length > 0;
        },
        canCreateTemplates() {
            return this.value?.TotalQuantity > 0 && this.value?.ShipmentCount > 0;
        },
        totalSubOrderQuantity() {
            return this.subOrders.reduce((sum, sub) => sum + (sub.OrderQuantity || 0), 0);
        },
        quantityMatch() {
            return Math.abs(this.totalSubOrderQuantity - (this.value?.TotalQuantity || 0)) < 0.01;
        },
        allSubOrdersHaveDate() {
            return this.subOrders.every((sub) => sub.OrderDate);
        },
        allSubOrdersComplete() {
            return this.subOrders.every((sub) => this.isSubOrderComplete(sub));
        },
        validationError() {
            if (this.subOrders.length === 0) return null;
            if (!this.quantityMatch) {
                return `Toplam miktar uyuşmuyor! Girilen: ${this.totalSubOrderQuantity.toLocaleString("tr-TR")} lt, Olması gereken: ${(this.value?.TotalQuantity || 0).toLocaleString("tr-TR")} lt`;
            }
            if (!this.allSubOrdersHaveDate) {
                return "Tüm alt siparişlerin termin tarihi girilmeli!";
            }
            if (!this.allSubOrdersComplete) {
                return "Tüm alt siparişlerin detayları doldurulmalıdır (Yakıt tipi, tedarikçi, şube vb.)!";
            }
            return null;
        },
        canSave() {
            return this.hasSubOrders && this.quantityMatch && this.allSubOrdersHaveDate && this.allSubOrdersComplete && this.value?.MainOrderNumber;
        },
    },
    methods: {
        updateSubOrderField(index, field, value) {
            this.$emit("update:subOrderField", { index, field, value });
        },
        createSubOrderTemplates() {
            if (!this.value) return;

            const count = parseInt(this.value.ShipmentCount) || 0;
            const totalQty = parseFloat(this.value.TotalQuantity) || 0;

            if (count <= 0 || totalQty <= 0) return;

            const templates = [];

            // Alt sipariş sayısı 1 olduğunda toplam miktar otomatik doldurulacak
            const autoFillQuantity = count === 1 ? totalQty : null;

            for (let i = 0; i < count; i++) {
                templates.push({
                    OrderNumber: null,
                    MainOrderNumber: this.value.MainOrderNumber,
                    OrderQuantity: autoFillQuantity,
                    OrderDate: null,
                    FuelTypeId: null,
                    FuelTypeName: null,
                    SupplierId: null,
                    VehiclePlateId: null,
                    TrailerPlateId: null,
                    FuelOrderBranchId: null,
                    DriverEmployeeId: null,
                    DriverEmployee: null,
                    PaymentType: null,
                    UnitPrice: null,
                    TransportAmount: null,
                    Vade: null,
                    FuelStorageCreditCardInformationId: null,
                    FuelStorageCreditCardLastPaymentDate: null,
                    FuelStorageSupplierAgreementId: null,
                    FuelStorageSupplierIskontoId: null,
                    FuelStorageSupplierIskontoRate: null,
                    _index: i,
                    _isDirty: false,
                });
            }

            this.$emit("update:subOrders", templates);
        },
        editSubOrder(index) {
            const sub = this.subOrders[index];
            this.$emit("edit-sub-order", {
                index: index,
                subOrder: { ...sub },
                mainOrderNumber: this.value.MainOrderNumber,
                isReadOnly: false,
            });
        },
        viewSubOrder(index) {
            const sub = this.subOrders[index];
            this.$emit("edit-sub-order", {
                index: index,
                subOrder: { ...sub },
                mainOrderNumber: this.value.MainOrderNumber,
                isReadOnly: true,
            });
        },
        isSubOrderComplete(sub) {
            return sub.OrderDate && sub.OrderQuantity > 0 && sub.FuelTypeId && sub.SupplierId && sub.FuelOrderBranchId;
        },
        closeModal() {
            this.$emit("close");
            this.$bvModal.hide("main-order-modal");
        },
        saveMainOrder() {
            if (!this.canSave) return;

            const payload = this.subOrders.map((sub) => ({
                ...sub,
                MainOrderNumber: this.value.MainOrderNumber,
            }));

            this.$emit("save", payload);
        },
        getFuelTypeBadgeClass(fuelTypeName) {
            if (!fuelTypeName) return "bg-secondary";
            const name = fuelTypeName.toLocaleLowerCase("tr-TR");
            if (name.includes("adblue")) return "bg-primary";
            if (name.includes("motorin") || name.includes("dizel")) return "bg-warning";
            return "bg-secondary";
        },
    },
};
</script>

<style scoped>
.main-order-form {
    max-height: 70vh;
    overflow-y: auto;
}
.is-required >>> label::after,
.is-required::v-deep label::after {
    content: " *";
    color: #dc3545;
    font-weight: 700;
}

/* Responsive tasarım düzeltmeleri */
@media (max-width: 768px) {
    .main-order-form .b-card-body {
        padding: 0.75rem;
    }
    
    .main-order-form .b-table {
        font-size: 0.75rem;
    }
    
    .main-order-form .b-table th,
    .main-order-form .b-table td {
        padding: 0.25rem 0.5rem;
        vertical-align: middle;
    }
    
    .main-order-form .b-table-simple {
        display: block;
        overflow-x: auto;
    }
    
    .main-order-form .btn-group-sm .btn {
        padding: 0.15rem 0.4rem;
        font-size: 0.75rem;
    }
    
    .main-order-form .badge {
        font-size: 0.7rem;
    }
    
    .main-order-form input.form-control-sm,
    .main-order-form .form-control {
        font-size: 0.875rem;
        padding: 0.25rem 0.5rem;
    }
}

@media (max-width: 576px) {
    .main-order-form .b-row {
        margin-left: -0.25rem;
        margin-right: -0.25rem;
    }
    
    .main-order-form .b-col {
        padding-left: 0.25rem;
        padding-right: 0.25rem;
    }
    
    .main-order-form .modal-header .modal-title {
        font-size: 1rem;
    }
    
    .main-order-form .b-card-header {
        flex-direction: column;
        gap: 0.5rem;
        align-items: flex-start !important;
    }
}
</style>
